import React, { useCallback, useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarDays, Clock3, PackageCheck, RefreshCw, TrendingUp } from "lucide-react";
import { getDashboardData } from "../api/dashboard";
import { readStoredUser } from "../utils/routing";
import "../styles/Dashboard.css";

// ─── Status bucket config ───────────────────────────────────────────────────
const BUCKETS = [
  {
    key: "pending",
    label: "Pending Approval",
    icon: "⏳",
    color: "#e8a838",
    bg: "#fef9ee",
    border: "#f5d98b",
  },
  {
    key: "l1_approved",
    label: "L1 Approved",
    icon: "✅",
    color: "#4caf84",
    bg: "#edfaf3",
    border: "#a8e6c8",
  },
  {
    key: "l2_approved",
    label: "Finance Approved",
    icon: "🏦",
    color: "#5b8dee",
    bg: "#eef3fd",
    border: "#adc5f5",
  },
  {
    key: "ordered",
    label: "Ordered",
    icon: "🛒",
    color: "#a06af9",
    bg: "#f5eeff",
    border: "#d4b3fc",
  },
  {
    key: "stores",
    label: "In Stores",
    icon: "📦",
    color: "#f06292",
    bg: "#fef0f5",
    border: "#f8bbd0",
  },
  {
    key: "delivered",
    label: "Awaiting Collection",
    icon: "🚚",
    color: "#26a69a",
    bg: "#e8f5f4",
    border: "#80cbc4",
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getBucketConfig = (key) => BUCKETS.find((b) => b.key === key);

const getLeadTimeCards = (summary = []) => {
  const supply = summary.find((stage) => stage.key === "supply_days");
  const supplier = summary.find((stage) => stage.key === "supplier_days");
  const internal = {
    key: "internal_days",
    label: "Internal Lead Time",
    average_days: supply?.average_days != null && supplier?.average_days != null
      ? Number((supply.average_days - supplier.average_days).toFixed(1))
      : null,
  };
  return [supply, internal, supplier].filter(Boolean);
};

// ─── Sub-components ──────────────────────────────────────────────────────────
function StatCard({ bucket, count, isActive, onClick }) {
  const cfg = getBucketConfig(bucket);
  return (
    <button
      className={`stat-card ${isActive ? "stat-card--active" : ""}`}
      style={{
        "--card-color": cfg.color,
        "--card-bg": cfg.bg,
        "--card-border": cfg.border,
      }}
      onClick={onClick}
      aria-pressed={isActive}
    >
      <span className="stat-card__icon">{cfg.icon}</span>
      <span className="stat-card__count">{count}</span>
      <span className="stat-card__label">{cfg.label}</span>
      {isActive && <span className="stat-card__pip" />}
    </button>
  );
}

function ItemRow({ item }) {
  return (
    <tr className="item-row">
      <td className="item-row__name">
        <span className="item-row__item-name">{item.item}</span>
        {item.description && (
          <span className="item-row__desc">{item.description}</span>
        )}
      </td>
      <td>
        {item.quantity} <span className="muted">{item.units}</span>
      </td>
      <td>{item.category_name || <span className="muted">—</span>}</td>
      <td>{item.location_name || <span className="muted">—</span>}</td>
      <td>{item.lot_full_number || <span className="muted">—</span>}</td>
      <td>{item.requester_name}</td>
      <td className="muted">{formatDate(item.created_at)}</td>
    </tr>
  );
}

function ItemTable({ items }) {
  if (!items || items.length === 0) {
    return (
      <div className="empty-state">
        <span className="empty-state__icon">🗂️</span>
        <p>No items in this stage</p>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table className="items-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Category</th>
            <th>Location</th>
            <th>Lot</th>
            <th>Requester</th>
            <th>Submitted</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <ItemRow key={item.id} item={item} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="stats-grid">
      {BUCKETS.map((b) => (
        <div key={b.key} className="skeleton-card" />
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const isRequester = readStoredUser()?.role === "requester";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");
  const [granularity, setGranularity] = useState("month");
  const [year, setYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const loadDashboard = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = { granularity, year };
    if (startDate) params.start = startDate;
    if (endDate) params.end = endDate;
    return getDashboardData(params)
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.detail || "Failed to load dashboard.");
        setLoading(false);
      });
  }, [granularity, year, startDate, endDate]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const refresh = () => {
    loadDashboard();
  };

  const activeCfg = getBucketConfig(activeTab);
  const activeItems = data?.items?.[activeTab] || [];
  const activeCount = data?.summary?.[activeTab]?.count ?? 0;

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Dashboard</h1>
          <p className="dashboard__subtitle">
            {isRequester ? "Live overview of your purchase requests" : "Live overview across all purchase requests"}
          </p>
        </div>
        <button
          className="refresh-btn"
          onClick={refresh}
          disabled={loading}
          title="Refresh"
        >
          <RefreshCw size={16} className={loading ? "spin" : ""} />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={refresh}>Retry</button>
        </div>
      )}

      {/* Stat Cards */}
      {loading && !data ? (
        <SkeletonCards />
      ) : (
        <div className="stats-grid">
          {BUCKETS.map((b) => (
            <StatCard
              key={b.key}
              bucket={b.key}
              count={data?.summary?.[b.key]?.count ?? 0}
              isActive={activeTab === b.key}
              onClick={() => setActiveTab(b.key)}
            />
          ))}
        </div>
      )}

      <section className="analytics-suite">
        <div className="analytics-suite__header">
          <div>
            <span className="analytics-eyebrow"><TrendingUp size={15} /> Procurement intelligence</span>
            <h2>Lead Time Analytics (working days)</h2>
            <p>{isRequester ? "Your" : "Organisation-wide"} performance from request creation through supplier delivery</p>
          </div>
        </div>
        <div className="analytics-filters">
          <label>View by
            <select value={granularity} onChange={(e) => setGranularity(e.target.value)}>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
              <option value="quarter">Quarterly</option>
              <option value="year">Yearly</option>
              <option value="custom">Custom range</option>
            </select>
          </label>
          {["week", "month", "quarter"].includes(granularity) && <label>Year
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[...new Set([year, ...(data?.lead_time?.available_years || [])])].sort((a, b) => b - a).map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>}
          {granularity === "custom" && <>
            <label>From<input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></label>
            <label>To<input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></label>
          </>}
        </div>

        <div className="lead-kpi-grid">
          {getLeadTimeCards(data?.lead_time?.stage_summary).map((stage, index) => {
            const icons = [<PackageCheck size={20} />, <Clock3 size={20} />, <CalendarDays size={20} />];
            return <article className={`lead-kpi lead-kpi--${index + 1}`} key={stage.key}>
              <div className="lead-kpi__top"><span>{icons[index]}</span><small>{stage.key === "internal_days" ? "End-to-end − Supplier delivery" : `${stage.requests} records`}</small></div>
              <strong>{stage.average_days ?? "—"}<em>{stage.average_days != null ? " working days" : ""}</em></strong>
              <p>{stage.label}</p>
            </article>;
          })}
        </div>

        {data?.lead_time?.bottleneck && <div className="bottleneck-note">
          <span><TrendingUp size={18} /></span>
          <div><strong>Current bottleneck: {data.lead_time.bottleneck.label}</strong><p>It has the longest average stage time at {data.lead_time.bottleneck.average_days} working days.</p></div>
        </div>}

        <div className="analytics-panel analytics-panel--wide">
          <div className="analytics-panel__heading"><div><h3>Lead-time trend</h3><p>Compare every stage over the selected timeline</p></div></div>
          <div className="analytics-chart" aria-label="Procurement stage lead time trend">
          {data?.lead_time?.series?.length ? (
            <ResponsiveContainer width="100%" height={330}>
              <LineChart data={data.lead_time.series} margin={{ top: 15, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis unit="d" tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => [`${value} working days`]} contentStyle={{ borderRadius: 10, borderColor: "#eadce4" }} />
                <Legend />
                <Line name="End-to-end supply" type="monotone" dataKey="supply_days" stroke="#a92f6f" strokeWidth={3} connectNulls />
                <Line name="Approvals" type="monotone" dataKey="approval_days" stroke="#a9ae50" strokeWidth={2} connectNulls />
                <Line name="Finance" type="monotone" dataKey="finance_days" stroke="#6f3c78" strokeWidth={2} connectNulls />
                <Line name="Supplier delivery" type="monotone" dataKey="supplier_days" stroke="#d66a9b" strokeWidth={2} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="empty-state"><p>No completed stages in this period</p></div>}
          </div>
        </div>

        <div className="analytics-split">
          <div className="analytics-panel">
            <div className="analytics-panel__heading"><div><h3>Stage comparison</h3><p>Average working days spent in each workflow stage</p></div></div>
            <ResponsiveContainer width="100%" height={270}>
              <BarChart data={(data?.lead_time?.stage_summary || []).slice(1)} layout="vertical" margin={{ left: 20, right: 25 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" unit="d" />
                <YAxis type="category" dataKey="label" width={105} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => [`${value} working days`, "Average"]} />
                <Bar dataKey="average_days" fill="#a92f6f" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="analytics-panel">
            <div className="analytics-panel__heading"><div><h3>Slowest categories</h3><p>End-to-end supply lead time by category</p></div></div>
            <ResponsiveContainer width="100%" height={270}>
              <BarChart data={data?.lead_time?.category_breakdown || []} layout="vertical" margin={{ left: 20, right: 25 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" unit="d" />
                <YAxis type="category" dataKey="category" width={105} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => [`${value} working days`, "Supply lead time"]} />
                <Bar dataKey="average_days" fill="#a9ae50" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Items Panel */}
      {data && (
        <div
          className="items-panel"
          style={{
            "--panel-color": activeCfg.color,
            "--panel-border": activeCfg.border,
            "--panel-bg": activeCfg.bg,
          }}
        >
          <div className="items-panel__header">
            <span className="items-panel__icon">{activeCfg.icon}</span>
            <div>
              <h2 className="items-panel__title">{activeCfg.label}</h2>
              <p className="items-panel__meta">
                Showing {activeItems.length} of {activeCount} item
                {activeCount !== 1 ? "s" : ""}
                {activeCount > 10 ? " (most recent 10)" : ""}
              </p>
            </div>
          </div>
          <ItemTable items={activeItems} />
        </div>
      )}
    </div>
  );
}
